import {useCallback, useState} from 'react'
import {ActionMenu, ActionList} from '@primer/react'
import {CalendarIcon} from '@primer/octicons-react'
import {PullsInboxMaxAge, PULLS_INBOX_MAX_AGE_LABELS} from '../../../types/routes-types'
import type {PullsInboxMaxAge as PullsInboxMaxAgeType} from '../../../types/routes-types'
import {saveInboxMaxPrAge} from '../../../helpers/inbox-preferences'
import {useAnalytics} from '@github-ui/use-analytics'

interface InboxPrAgeFilterProps {
  selectedMaxPrAge: PullsInboxMaxAgeType
  onMaxPrAgeChange: (maxPrAge: PullsInboxMaxAgeType) => void
}

const AGE_OPTIONS = [
  PullsInboxMaxAge.OneMonth,
  PullsInboxMaxAge.ThreeMonths,
  PullsInboxMaxAge.OneYear,
  PullsInboxMaxAge.None,
] as const

export function InboxPrAgeFilter({selectedMaxPrAge, onMaxPrAgeChange}: InboxPrAgeFilterProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const [isSaving, setIsSaving] = useState(false)

  const handleSelect = useCallback(
    async (maxPrAge: PullsInboxMaxAgeType) => {
      if (maxPrAge === selectedMaxPrAge) return

      sendAnalyticsEvent('inbox.click', 'AGE_FILTER_OPTION', {selectedMaxPrAge: maxPrAge})

      onMaxPrAgeChange(maxPrAge)

      setIsSaving(true)
      try {
        await saveInboxMaxPrAge(maxPrAge)
      } catch {
        onMaxPrAgeChange(selectedMaxPrAge)
      } finally {
        setIsSaving(false)
      }
    },
    [selectedMaxPrAge, sendAnalyticsEvent, onMaxPrAgeChange],
  )

  return (
    <ActionMenu>
      <ActionMenu.Button
        leadingVisual={CalendarIcon}
        disabled={isSaving}
        aria-label={`Filter by age: ${PULLS_INBOX_MAX_AGE_LABELS[selectedMaxPrAge]}`}
      >
        {`Updated: ${PULLS_INBOX_MAX_AGE_LABELS[selectedMaxPrAge]}`}
      </ActionMenu.Button>
      <ActionMenu.Overlay width="auto">
        <ActionList selectionVariant="single">
          {AGE_OPTIONS.map(option => (
            <ActionList.Item key={option} selected={option === selectedMaxPrAge} onSelect={() => handleSelect(option)}>
              {PULLS_INBOX_MAX_AGE_LABELS[option]}
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
